
/**
 * Utility functions for handling network connections and WebSocket issues
 */

// Check if the browser is online
export const isOnline = () => navigator.onLine;

// Helper to identify connection type if available
export const getConnectionInfo = () => {
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    return {
      type: connection?.effectiveType || 'unknown',
      downlinkMbps: connection?.downlink || 0,
      rtt: connection?.rtt || 0,
      saveData: connection?.saveData || false
    };
  }
  return { type: 'unknown', downlinkMbps: 0, rtt: 0, saveData: false };
};

// Safer WebSocket connection function with fallbacks and error handling
export const createSafeWebSocket = (
  url: string, 
  options: {
    onOpen?: (event: Event) => void;
    onMessage?: (event: MessageEvent) => void;
    onClose?: (event: CloseEvent) => void;
    onError?: (event: Event) => void;
    maxRetries?: number;
    retryDelay?: number;
  } = {}
) => {
  const {
    onOpen,
    onMessage,
    onClose,
    onError,
    maxRetries = 3,
    retryDelay = 2000
  } = options;
  
  let ws: WebSocket | null = null;
  let retries = 0;
  
  const connect = () => {
    // Use secure WebSocket protocol
    const secureUrl = url.replace('ws://', 'wss://');
    
    try {
      console.log(`Attempting WebSocket connection to ${secureUrl}, attempt ${retries + 1}/${maxRetries}`);
      ws = new WebSocket(secureUrl);
      
      ws.addEventListener('open', (event) => {
        retries = 0;
        console.log('WebSocket connection established');
        if (onOpen) onOpen(event);
      });
      
      ws.addEventListener('message', (event) => {
        if (onMessage) onMessage(event);
      });
      
      ws.addEventListener('close', (event) => {
        console.log('WebSocket connection closed', event);
        
        if (onClose) onClose(event);
        
        // Retry connection if necessary and not deliberately closed
        if (retries < maxRetries && event.code !== 1000) {
          retries++;
          console.log(`Retrying WebSocket connection in ${retryDelay}ms...`);
          setTimeout(connect, retryDelay);
        }
      });
      
      ws.addEventListener('error', (event) => {
        console.error('WebSocket error:', event);
        if (onError) onError(event);
      });
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  };
  
  // Initial connection
  connect();
  
  return {
    send: (data: string | ArrayBufferLike | Blob | ArrayBufferView) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(data);
        return true;
      }
      return false;
    },
    close: () => {
      if (ws) {
        retries = maxRetries; // Prevent auto-reconnect
        ws.close();
      }
    },
    getStatus: () => ws ? ws.readyState : -1
  };
};

/**
 * Convert HTTP protocol error codes to human-readable messages
 */
export const getNetworkErrorMessage = (error?: any) => {
  if (!error) return 'Unknown network error';
  
  // Extract error code from various error formats
  const errorCode = error.code || error.status || 
    (typeof error === 'string' && error.includes('ERR_') ? error : null);
  
  switch (errorCode) {
    case 'ERR_HTTP2_PROTOCOL_ERROR':
      return 'HTTP/2 protocol error. This could be due to network conditions or server configuration.';
    case 'ERR_QUIC_PROTOCOL_ERROR':
      return 'QUIC protocol error. This could be due to network conditions or firewall settings.';
    case 400:
      return 'Bad request. The server cannot process the request due to client error.';
    case 401:
      return 'Unauthorized. Authentication is required and has failed.';
    case 403:
      return 'Forbidden. You do not have permission to access this resource.';
    case 404:
      return 'Not found. The requested resource could not be found.';
    case 429:
      return 'Too many requests. You have sent too many requests in a given amount of time.';
    case 500:
      return 'Internal server error. The server has encountered a situation it does not know how to handle.';
    case 502:
      return 'Bad gateway. The server received an invalid response from an upstream server.';
    case 503:
      return 'Service unavailable. The server is currently unable to handle the request due to temporary overloading or maintenance.';
    case 504:
      return 'Gateway timeout. The server did not receive a timely response from an upstream server.';
    default:
      if (error.message) return error.message;
      return 'An unexpected network error occurred. Please try again later.';
  }
};
